import { useState } from 'react'
import { X, Loader2, Plus, Square } from 'lucide-react'
import toast from 'react-hot-toast'
import { kioskApi } from './kiosk-api'
import KioskNumpad from './KioskNumpad'

type Props = {
  etapa_op_id: number
  modo: 'parcial' | 'concluir'
  produtoNome: string
  onClose: () => void
  onDone: () => void
}

export default function KioskApontamento({ etapa_op_id, modo, produtoNome, onClose, onDone }: Props) {
  const [campo, setCampo] = useState<'qtd' | 'peso'>('qtd')
  const [qtd, setQtd] = useState('')
  const [peso, setPeso] = useState('')
  const [obs, setObs] = useState('')
  const [loading, setLoading] = useState(false)

  async function submit() {
    if (!qtd && !peso) {
      toast.error('Informe quantidade ou peso')
      return
    }
    setLoading(true)
    try {
      const url = modo === 'parcial' ? '/kiosk/apontamento-parcial' : '/kiosk/etapa/concluir'
      await kioskApi.post(url, {
        etapa_op_id,
        quantidade: parseFloat(qtd) || 0,
        peso_kg: parseFloat(peso) || 0,
        observacao: obs || undefined,
      })
      toast.success(modo === 'parcial' ? 'Apontamento registrado' : 'Etapa concluída')
      onDone()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro')
    } finally {
      setLoading(false)
    }
  }

  const cor = modo === 'parcial' ? 'violet' : 'emerald'
  const valorAtivo = campo === 'qtd' ? qtd : peso
  const setAtivo = campo === 'qtd' ? setQtd : setPeso

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center">
      <div className="bg-white w-full md:max-w-md rounded-t-2xl md:rounded-2xl p-5 space-y-4 max-h-[95vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              {modo === 'parcial' ? <><Plus className="h-5 w-5 text-violet-600" /> Apontar produção</>
                                  : <><Square className="h-5 w-5 text-emerald-600" /> Concluir etapa</>}
            </h2>
            <div className="text-sm text-ink-500 line-clamp-1">{produtoNome}</div>
          </div>
          <button onClick={onClose}><X className="h-6 w-6" /></button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setCampo('qtd')}
            className={`h-14 rounded-xl font-semibold transition ${
              campo === 'qtd' ? `bg-${cor}-600 text-white` : 'bg-ink-100 text-ink-700'
            }`}
            style={campo === 'qtd' ? { backgroundColor: modo === 'parcial' ? '#7c3aed' : '#059669', color: 'white' } : {}}
          >
            <div className="text-xs uppercase opacity-80">Quantidade</div>
            <div className="text-2xl">{qtd || '0'}</div>
          </button>
          <button
            onClick={() => setCampo('peso')}
            className={`h-14 rounded-xl font-semibold transition ${
              campo === 'peso' ? 'text-white' : 'bg-ink-100 text-ink-700'
            }`}
            style={campo === 'peso' ? { backgroundColor: modo === 'parcial' ? '#7c3aed' : '#059669', color: 'white' } : {}}
          >
            <div className="text-xs uppercase opacity-80">Peso (kg)</div>
            <div className="text-2xl">{peso || '0'}</div>
          </button>
        </div>

        <KioskNumpad value={valorAtivo} onChange={setAtivo} allowDecimal maxLen={9} />

        <input
          value={obs}
          onChange={(e) => setObs(e.target.value)}
          placeholder="Observação (opcional)"
          className="w-full h-12 px-4 border-2 border-ink-200 rounded-xl text-base"
        />

        <button
          onClick={submit}
          disabled={loading || (!qtd && !peso)}
          className={`w-full h-16 text-white font-bold text-lg rounded-xl flex items-center justify-center gap-2 disabled:opacity-40 ${
            modo === 'parcial' ? 'bg-violet-600 hover:bg-violet-700' : 'bg-emerald-600 hover:bg-emerald-700'
          }`}
        >
          {loading ? <Loader2 className="h-6 w-6 animate-spin" /> :
            modo === 'parcial' ? 'Registrar apontamento' : 'Concluir etapa'}
        </button>
      </div>
    </div>
  )
}
